import { useState } from "react";

import type { DocumentFormat } from "@/api/quoteDocuments";
import {
  PennButton,
  PennField,
  PennModal,
  PennTextarea,
} from "@/components/ui";
import { Cad3dPanel } from "@/components/cad3d/Cad3dPanel";
import { useDownloadSupportResponse } from "@/hooks/useQuoteDocuments";
import {
  useRespondAndCloseTask,
  useTask,
  useTaskApplications,
} from "@/hooks/useTasks";
import { useAppSelector } from "@/stores/hooks";
import type { TaskApplicationLink } from "@/api/tasks";

// =============================================================================
// CR-005 "Response to General support" popup (Option-2 design). Opened from the
// Tasks modal for a message-only support request (no machine, no applications).
// Internal staff (canWorkForOthers) get the response form + "Mark as Closed"
// (which stamps the task Quoted per CR-005) and PDF/Word downloads; everyone
// sees the read-only response thread.
// =============================================================================

type Props = { taskId: string; onClose: () => void };

function errMsg(e: unknown, fallback: string): string {
  const msg = (e as { response?: { data?: { error?: { message?: string } } } })
    ?.response?.data?.error?.message;
  return msg || (e instanceof Error ? e.message : fallback);
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function Detail({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase text-gray-500">
        {label}
      </div>
      <div className="text-sm text-gray-800 dark:text-gray-100">
        {value?.trim() ? value : "—"}
      </div>
    </div>
  );
}

function ApplicationsSection({
  items,
  canGenerateQuote,
}: {
  items: TaskApplicationLink[];
  canGenerateQuote: boolean;
}) {
  if (items.length === 0) return null;
  return (
    <section>
      <div className="mb-1 text-[11px] font-semibold uppercase text-gray-500">
        Applications
      </div>
      <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
        <table className="w-full text-sm text-gray-800 dark:text-gray-100">
          <thead className="bg-gray-50 text-[11px] uppercase text-gray-500 dark:bg-gray-800/40">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Application</th>
              <th className="px-3 py-2 text-right font-semibold">Annual vol.</th>
              <th className="px-3 py-2 text-right font-semibold">Batch</th>
              <th className="px-3 py-2 text-left font-semibold">Fasteners</th>
              {/* 3D part — internal artifact, same gate/black button as the
                  machine quotation's RFQ-context panel (Cad3dPanel). */}
              {canGenerateQuote && (
                <th className="px-3 py-2 text-left font-semibold">3D part</th>
              )}
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr
                key={a.appId}
                className="border-t border-gray-100 dark:border-gray-800"
              >
                <td className="px-3 py-2">
                  {a.name?.trim() || a.applicationNumber || "Application"}
                </td>
                <td className="px-3 py-2 text-right">
                  {a.annualVolume ?? "—"}
                </td>
                <td className="px-3 py-2 text-right">{a.batchSize ?? "—"}</td>
                <td className="px-3 py-2">
                  {a.fasteners && a.fasteners.length > 0
                    ? a.fasteners.join(", ")
                    : "—"}
                </td>
                {canGenerateQuote && (
                  <td className="px-3 py-2">
                    {a.savedId ? (
                      <Cad3dPanel
                        applicationId={a.savedId}
                        readOnly
                        showViewer={false}
                        viewerLinkLabel="Analyze 3D part"
                        viewerLinkPath="/cad/compare"
                        viewerLinkAsButton
                        viewerLinkNewWindow
                      />
                    ) : (
                      "—"
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function SupportResponseModal({ taskId, onClose }: Props) {
  const { data: task, isLoading } = useTask(taskId);
  const { data: applications } = useTaskApplications(taskId);
  const isStaff = useAppSelector((s) => !!s.auth.user?.role?.canWorkForOthers);
  const canGenerateQuote = useAppSelector(
    (s) => !!s.auth.user?.role?.canGenerateQuote,
  );
  const respond = useRespondAndCloseTask();
  const download = useDownloadSupportResponse();
  const [response, setResponse] = useState("");
  const [error, setError] = useState<string | null>(null);

  const isResponded = task?.quotedAt != null;
  const customerName =
    [task?.requestedByFirstName, task?.requestedByLastName]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    task?.requestedByEmail ||
    "—";

  const submit = () => {
    setError(null);
    const text = response.trim();
    if (!text) {
      setError("Enter response comments before closing this task.");
      return;
    }
    respond.mutate(
      { taskId, response: text },
      {
        onSuccess: () => setResponse(""),
        onError: (e) =>
          setError(errMsg(e, "Could not respond and close this task.")),
      },
    );
  };

  const doDownload = (format: DocumentFormat) => {
    setError(null);
    download.mutate(
      { taskId, format },
      {
        onError: (e) =>
          setError(
            errMsg(
              e,
              `Could not download the ${format.toUpperCase()} response.`,
            ),
          ),
      },
    );
  };

  return (
    <PennModal
      open
      onClose={onClose}
      title="Response to General support"
      maxWidth="max-w-2xl"
      zIndex="z-[130]"
    >
      {isLoading || !task ? (
        <div className="px-6 py-10 text-center text-sm text-gray-500">
          Loading…
        </div>
      ) : (
        <div className="space-y-5 px-6 py-5">
          <section className="rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-800 dark:bg-gray-800/40">
            <div className="mb-2 text-[11px] font-semibold uppercase text-gray-500">
              Customer details
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 md:grid-cols-3">
              <Detail label="Name" value={customerName} />
              <Detail label="Email" value={task.requestedByEmail} />
              <Detail label="Phone" value={task.requestedByPhone} />
              <Detail label="Company" value={task.requestedByCompanyName} />
              <Detail
                label="Company address"
                value={task.requestedByCompanyAddress}
              />
              <Detail label="Department" value={task.requestedByDepartment} />
              <Detail label="Region" value={task.requestedByRegion} />
            </div>
          </section>

          <ApplicationsSection
            items={applications ?? []}
            canGenerateQuote={canGenerateQuote}
          />

          <section>
            <div className="mb-1 text-[11px] font-semibold uppercase text-gray-500">
              Original request
            </div>
            <div className="whitespace-pre-wrap break-words rounded-xl border border-gray-200 bg-white p-3 text-sm text-gray-800 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-100">
              {task.comment?.trim() || "—"}
            </div>
          </section>

          {(task.supportResponses ?? []).length > 0 && (
            <section className="space-y-2">
              <div className="text-[11px] font-semibold uppercase text-gray-500">
                Response
              </div>
              {(task.supportResponses ?? []).map((item, index) => (
                <div
                  key={`${item.respondedAt}-${index}`}
                  className="rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-800 dark:bg-gray-800/40"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
                    <span className="font-semibold text-gray-700 dark:text-gray-200">
                      {item.responderName ??
                        item.responderEmail ??
                        "PennEngineering Support"}
                    </span>
                    <span>{fmtDate(item.respondedAt)}</span>
                  </div>
                  <p className="mt-2 whitespace-pre-wrap break-words text-sm text-gray-800 dark:text-gray-100">
                    {item.response}
                  </p>
                </div>
              ))}
            </section>
          )}

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300">
              {error}
            </div>
          )}

          {isStaff && !isResponded && (
            <section className="space-y-3">
              <PennField
                label="Your response comments"
                htmlFor="support-response-comments"
              >
                <PennTextarea
                  id="support-response-comments"
                  rows={5}
                  maxLength={5000}
                  value={response}
                  onChange={(event) => setResponse(event.target.value)}
                  placeholder="Add your response, resolution details, or notes for the customer here."
                />
              </PennField>
              <div className="flex justify-end">
                <PennButton
                  variant="primary"
                  size="sm"
                  onClick={submit}
                  disabled={respond.isPending || response.trim().length === 0}
                >
                  {respond.isPending ? "Closing…" : "Mark as Closed"}
                </PennButton>
              </div>
            </section>
          )}

          {isResponded && (
            <section className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-800 dark:bg-emerald-900/20">
              <span className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
                This support request has been responded to and marked Quoted.
              </span>
              {/* Download is staff-only (backend gates the render on
                  canWorkForOthers) — hidden from external customers. */}
              {isStaff && (
                <div className="flex items-center gap-2">
                  <PennButton
                    variant="secondary"
                    size="sm"
                    onClick={() => doDownload("pdf")}
                    disabled={download.isPending}
                  >
                    Download Response
                  </PennButton>
                  <PennButton
                    variant="secondary"
                    size="sm"
                    onClick={() => doDownload("docx")}
                    disabled={download.isPending}
                  >
                    Download Response in Word
                  </PennButton>
                </div>
              )}
            </section>
          )}
        </div>
      )}
    </PennModal>
  );
}
